/* ---------------------------------------------------------------------------
   How long the kabbalos were accepted for
   ---------------------------------------------------------------------------
   One radio answer plus, for `custom` alone, a day count. Every surface that
   shows it reads through here — the public form, the Add / Edit modals, the
   submissions panel, the design preview and the live board — so the wording is
   the same everywhere and a stray value can never render as a raw enum key.
   -------------------------------------------------------------------------- */

import { KabbalahDuration_N } from '@/utils/enums';

export const KABBALAH_CUSTOM_DAYS_MIN = 1;
export const KABBALAH_CUSTOM_DAYS_MAX = 3650;

export interface KabbalahDurationOption {
  value: KabbalahDuration_N;
  label: string;
}

/** The three answers, in the order the form shows them. */
export const KABBALAH_DURATION_OPTIONS: KabbalahDurationOption[] = [
  { value: KabbalahDuration_N.oneYear, label: 'One full year' },
  { value: KabbalahDuration_N.fortyDays, label: 'Forty days or longer' },
  { value: KabbalahDuration_N.custom, label: 'Custom - number of days' },
];

export const isKabbalahDuration = (value: unknown): value is KabbalahDuration_N =>
  (Object.values(KabbalahDuration_N) as string[]).includes(String(value));

/** Anything unrecognised reads as the form's own default answer. */
export const normalizeKabbalahDuration = (value: unknown): KabbalahDuration_N =>
  isKabbalahDuration(value) ? value : KabbalahDuration_N.oneYear;

/**
 * A clamped whole number for `custom`, and null for the two fixed durations,
 * which carry their length in the answer alone. Null for a `custom` that came
 * with nothing usable — the caller's validation is what turns that into an
 * error, so this never invents a length.
 */
export const normalizeKabbalahCustomDays = (
  durationType: unknown,
  value: unknown
): number | null => {
  if (normalizeKabbalahDuration(durationType) !== KabbalahDuration_N.custom) return null;

  // Checked before the conversion: Number(null) and Number('') are both 0, which
  // is finite, so "no answer" would otherwise clamp to the minimum and read back
  // as a deliberate one-day kabbalah.
  if (value === null || value === undefined || String(value).trim() === '') return null;

  const days = Number(value);
  if (!Number.isFinite(days)) return null;

  return Math.min(
    Math.max(Math.round(days), KABBALAH_CUSTOM_DAYS_MIN),
    KABBALAH_CUSTOM_DAYS_MAX
  );
};

/** Whether a duration answer is complete enough to submit. */
export const isCompleteKabbalahDuration = (
  durationType: unknown,
  customDays: unknown
): boolean => {
  if (!isKabbalahDuration(durationType)) return false;
  if (durationType !== KabbalahDuration_N.custom) return true;
  return normalizeKabbalahCustomDays(durationType, customDays) !== null;
};

/**
 * How the duration reads on a board or in the review panel: the chosen answer,
 * with the day count folded in when there is one.
 */
export const kabbalahDurationLabel = (
  durationType: unknown,
  customDays: unknown = null
): string => {
  const duration = normalizeKabbalahDuration(durationType);

  if (duration === KabbalahDuration_N.custom) {
    const days = normalizeKabbalahCustomDays(duration, customDays);
    if (days === null) return 'Custom';
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  return (
    KABBALAH_DURATION_OPTIONS.find((option) => option.value === duration)?.label
    ?? 'One full year'
  );
};
